import { create } from 'zustand';
import { subscribeWithSelector } from 'zustand/middleware';

interface LoadingState {
  // Global loading states
  globalLoading: boolean;
  loadingText: string;
  
  // Specific operation loading states
  loadingStates: Record<string, boolean>;
  loadingTexts: Record<string, string>;
  
  // Actions
  setGlobalLoading: (loading: boolean, text?: string) => void;
  setOperationLoading: (operation: string, loading: boolean, text?: string) => void;
  clearOperationLoading: (operation: string) => void;
  clearAllLoading: () => void;
  
  // Computed getters
  isAnyLoading: () => boolean;
  getOperationLoading: (operation: string) => boolean;
  getOperationText: (operation: string) => string;
}

export const useLoadingStore = create<LoadingState>()(
  subscribeWithSelector((set, get) => ({
    // Initial state
    globalLoading: false,
    loadingText: '',
    loadingStates: {},
    loadingTexts: {},
    
    // Actions
    setGlobalLoading: (loading, text = '') => set({
      globalLoading: loading,
      loadingText: text,
    }),
    
    setOperationLoading: (operation, loading, text = '') => set((state) => ({
      loadingStates: {
        ...state.loadingStates,
        [operation]: loading,
      },
      loadingTexts: {
        ...state.loadingTexts,
        [operation]: text,
      },
    })),
    
    clearOperationLoading: (operation) => set((state) => {
      const newLoadingStates = { ...state.loadingStates };
      const newLoadingTexts = { ...state.loadingTexts };
      delete newLoadingStates[operation];
      delete newLoadingTexts[operation];
      
      return {
        loadingStates: newLoadingStates,
        loadingTexts: newLoadingTexts,
      };
    }),
    
    clearAllLoading: () => set({
      globalLoading: false,
      loadingText: '',
      loadingStates: {},
      loadingTexts: {},
    }),
    
    // Computed getters
    isAnyLoading: () => {
      const state = get();
      return state.globalLoading || Object.values(state.loadingStates).some(Boolean);
    },
    
    getOperationLoading: (operation) => {
      const state = get();
      return state.loadingStates[operation] || false;
    },
    
    getOperationText: (operation) => {
      const state = get();
      return state.loadingTexts[operation] || '';
    },
  }))
);

// Loading operation constants
export const LOADING_OPERATIONS = {
  AUTH_LOGIN: 'auth_login',
  AUTH_LOGOUT: 'auth_logout',
  WATCHLIST_LOAD: 'watchlist_load',
  WATCHLIST_REFRESH: 'watchlist_refresh',
  COMPANY_SUMMARY: 'company_summary',
  INDUSTRY_SUMMARY: 'industry_summary',
  COMPANY_ADD: 'company_add',
  COMPANY_REMOVE: 'company_remove',
  INDUSTRY_ADD: 'industry_add',
  INDUSTRY_REMOVE: 'industry_remove',
} as const;